import React, { useState, useEffect } from 'react';
import { NavLink, Link, Outlet } from 'react-router-dom';
import api from '../core/api';
import { usePlan } from '../hooks/usePlan';
import TrialExpiredOverlay from './TrialExpiredOverlay';
import SupportChat from './SupportChat';

const Layout: React.FC = () => {
    const plan = usePlan();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [feedbackSent, setFeedbackSent] = useState(false);
    const [notifications] = useState<{id: string; message: string; time: string; read: boolean}[]>([]);
    const userData = JSON.parse(localStorage.getItem('user-data') || '{}');
    const isAgent = userData.role === 'agent';
    const [isImpersonating, setIsImpersonating] = useState(false);
    const [originalOrgName, setOriginalOrgName] = useState('');

    useEffect(() => {
        const originalToken = localStorage.getItem('original-super-admin-token');
        const orgName = localStorage.getItem('impersonated-org-name');
        if (originalToken) {
            setIsImpersonating(true);
            setOriginalOrgName(orgName || 'Cliente');
        }
    }, []);

    const handleExitImpersonation = () => {
        const originalToken = localStorage.getItem('original-super-admin-token');
        const originalOrgId = localStorage.getItem('original-org-id');
        
        if (originalToken) {
            localStorage.setItem('auth-token', originalToken);
            if (originalOrgId) {
                localStorage.setItem('org-id', originalOrgId);
            } else {
                localStorage.removeItem('org-id');
            }
            // Limpiar datos de suplantacion
            localStorage.removeItem('original-super-admin-token');
            localStorage.removeItem('original-org-id');
            localStorage.removeItem('impersonated-org-name');
            localStorage.removeItem('impersonated-admin-email');
            
            // Re-auth me del admin para restaurar perfil local
            api.get('/auth/me').then(res => {
                const u = res.data?.user || res.data || {};
                localStorage.setItem('user-data', JSON.stringify(u));
                window.location.href = '/admin/orgs'; // Redirigir al panel de cuentas de superadmin
            }).catch(() => {
                window.location.href = '/admin/orgs';
            });
        }
    };

    // Initialize theme — dark by default, respects localStorage preference
    useEffect(() => {
        const saved = localStorage.getItem('theme');
        if (saved === 'light') {
            document.documentElement.classList.remove('dark');
        } else {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        }
    }, []);

    const closeAllMenus = () => {
        setShowUserMenu(false);
        setShowNotifications(false);
    };

    const handleSendFeedback = async () => {
        if (!feedbackText.trim()) return;
        try {
            await api.post('/feedback', {
                message: feedbackText,
                userId: userData.id,
                userEmail: userData.email,
                userName: userData.name
            });
        } catch (error) {
            console.error('Error sending feedback:', error);
        }
        setFeedbackSent(true);
        setTimeout(() => {
            setShowFeedbackModal(false);
            setFeedbackText('');
            setFeedbackSent(false);
        }, 2000);
    };

    const handleLogout = () => {
        localStorage.removeItem('org-id');
        localStorage.removeItem('auth-token');
        localStorage.removeItem('user-data');
        window.location.reload();
    };

    return (
        <>
        <div className="min-h-screen bg-background-light dark:bg-background-dark flex flex-col transition-colors">
            {/* Aurora background — mismo glow que Landing/Login/Register/Onboarding,
                puesto una sola vez acá para que cubra toda la app autenticada */}
            <div className="aurora-bg dark:block hidden" aria-hidden="true">
                <div className="aurora-blob"></div>
                <div className="aurora-blob"></div>
                <div className="aurora-blob"></div>
            </div>

            {/* Impersonation Banner */}
            {isImpersonating && (
                <div className="bg-gradient-to-r from-primary via-primary-light to-primary px-4 py-2 flex items-center justify-between text-white text-xs sm:text-sm font-semibold shadow-md z-[60] border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px] animate-pulse">support_agent</span>
                        <span>
                            Modo Soporte: Suplantando cuenta de <strong className="underline decoration-white/40">{originalOrgName}</strong>
                        </span>
                    </div>
                    <button
                        onClick={handleExitImpersonation}
                        className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white border border-white/20 rounded-lg font-bold text-[11px] sm:text-xs transition-colors flex items-center gap-1 active:scale-95"
                    >
                        <span className="material-symbols-outlined text-[13px]">logout</span>
                        Salir de modo soporte
                    </button>
                </div>
            )}

            {/* Top Header */}
            <header className="sticky top-0 z-50 bg-white/80 dark:bg-background-dark/90 backdrop-blur-lg border-b border-slate-200/60 dark:border-white/5">
                <div className="flex items-center justify-between px-4 py-3">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2">
                        <img src="/logo.png" alt="Diana IA" className="w-9 h-9 rounded-xl object-contain" />
                        <span className="font-bold text-lg text-slate-900 dark:text-white">Diana IA</span>
                    </Link>

                    {/* Right Actions */}
                    <div className="flex items-center gap-3">
                        {/* Feedback Button */}
                        <button
                            onClick={() => setShowFeedbackModal(true)}
                            className="flex items-center gap-1 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors text-sm px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                            title="Enviar feedback"
                        >
                            <span className="material-symbols-outlined text-[20px]">feedback</span>
                            <span className="hidden sm:inline font-medium">Feedback</span>
                        </button>

                        {/* Notifications */}
                        <div className="relative">
                            <button
                                onClick={() => { closeAllMenus(); setShowNotifications(!showNotifications); }}
                                className="relative p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                            >
                                <span className="material-symbols-outlined text-[22px]">notifications</span>
                                {notifications.filter(n => !n.read).length > 0 && (
                                    <span className="absolute top-1 right-1 w-2 h-2 bg-brand-danger/90 rounded-full"></span>
                                )}
                            </button>

                            {showNotifications && (
                                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-card rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 z-50 overflow-hidden">
                                    <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                                        <h3 className="font-semibold text-slate-900 dark:text-white">Notificaciones</h3>
                                        {notifications.length > 0 && (
                                            <button className="text-xs text-primary hover:underline">Marcar todas como leídas</button>
                                        )}
                                    </div>
                                    <div className="max-h-80 overflow-y-auto">
                                        {notifications.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                                                <span className="material-symbols-outlined text-[40px] mb-2">notifications_off</span>
                                                <p className="text-sm">No tienes notificaciones</p>
                                            </div>
                                        ) : (
                                            notifications.map(notif => (
                                                <div key={notif.id} className={`px-4 py-3 border-b border-slate-50 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer ${!notif.read ? 'bg-primary/5' : ''}`}>
                                                    <p className="text-sm text-slate-700 dark:text-slate-200">{notif.message}</p>
                                                    <p className="text-xs text-slate-400 mt-1">{notif.time}</p>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* User Avatar */}
                        <div className="relative">
                            <button
                                onClick={() => { closeAllMenus(); setShowUserMenu(!showUserMenu); }}
                                className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-semibold text-sm ring-2 ring-white shadow-md"
                            >
                                {userData.name?.charAt(0) || 'U'}
                            </button>

                            {/* User Menu Dropdown */}
                            {showUserMenu && (
                                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-card rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 py-2 z-50">
                                    <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                                        <p className="font-semibold text-slate-900 dark:text-white text-sm">{userData.name || 'Usuario'}</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">{userData.email}</p>
                                        {plan && (
                                            <span className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                                plan.accountStatus === 'trial_active'
                                                    ? 'bg-amber-50 text-amber-600 border border-amber-100'
                                                    : plan.accountStatus === 'trial_expired'
                                                    ? 'bg-orange-50 text-orange-600 border border-orange-100'
                                                    : plan.hasProFeatures
                                                    ? 'bg-primary/8 text-primary border border-primary/12'
                                                    : 'bg-slate-100 text-slate-500 border border-slate-200'
                                            }`}>
                                                {plan.accountStatus === 'trial_active' ? '🧪' : plan.accountStatus === 'trial_grace' ? '⏳' : plan.accountStatus === 'trial_expired' ? '⚠️' : plan.hasProFeatures ? '⭐' : '○'} {plan.planLabel}
                                            </span>
                                        )}
                                    </div>
                                    <Link
                                        to="/fugas"
                                        onClick={() => setShowUserMenu(false)}
                                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">monitoring</span>
                                        Dónde se escapa el dinero
                                    </Link>
                                    <Link
                                        to="/cobros"
                                        onClick={() => setShowUserMenu(false)}
                                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">payments</span>
                                        Cobros
                                    </Link>
                                    <Link
                                        to="/lista-espera"
                                        onClick={() => setShowUserMenu(false)}
                                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">groups</span>
                                        Lista de espera
                                    </Link>
                                    <Link
                                        to="/settings"
                                        onClick={() => setShowUserMenu(false)}
                                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">settings</span>
                                        Configuración
                                    </Link>
                                    {userData.role === 'super_admin' && (
                                        <Link
                                            to="/monitoring"
                                            onClick={() => setShowUserMenu(false)}
                                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">monitoring</span>
                                            Monitoreo y uso IA
                                        </Link>
                                    )}
                                    {userData.role === 'super_admin' && (
                                        <Link
                                            to="/security"
                                            onClick={() => setShowUserMenu(false)}
                                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">shield</span>
                                            Seguridad
                                        </Link>
                                    )}
                                    {userData.role === 'super_admin' && (
                                        <Link
                                            to="/meta-ads"
                                            onClick={() => setShowUserMenu(false)}
                                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                                        >
                                            <span className="w-[18px] h-[18px] rounded bg-blue-600 flex items-center justify-center text-white text-[11px] font-black">f</span>
                                            Meta Ads
                                        </Link>
                                    )}
                                    {userData.role === 'super_admin' && (
                                        <>
                                            <Link
                                                to="/admin/marketing-leads"
                                                onClick={() => setShowUserMenu(false)}
                                                className="flex items-center gap-2 px-4 py-2 text-sm text-primary hover:bg-primary/8"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">campaign</span>
                                                Marketing Leads
                                            </Link>
                                            <Link
                                                to="/admin/outreach"
                                                onClick={() => setShowUserMenu(false)}
                                                className="flex items-center gap-2 px-4 py-2 text-sm text-violet-600 hover:bg-violet-50"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">contacts</span>
                                                Prospeccion
                                            </Link>
                                            <Link
                                                to="/admin/pagos"
                                                onClick={() => setShowUserMenu(false)}
                                                className="flex items-center gap-2 px-4 py-2 text-sm text-violet-600 hover:bg-violet-50"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">payments</span>
                                                Pagos
                                            </Link>
                                            <Link
                                                to="/admin/orgs"
                                                onClick={() => setShowUserMenu(false)}
                                                className="flex items-center gap-2 px-4 py-2 text-sm text-primary hover:bg-primary/8"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">apartment</span>
                                                Panel de Cuentas
                                            </Link>
                                        </>
                                    )}
                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-2 px-4 py-2 text-sm text-brand-danger hover:bg-brand-danger/8 w-full"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">logout</span>
                                        Cerrar Sesión
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Trial Expired Overlay — shown on all pages */}
            {(plan?.accountStatus === 'trial_expired' || plan?.accountStatus === 'trial_grace') && <TrialExpiredOverlay />}

            {/* Main Content */}
            <main className="flex-1 overflow-auto pb-20">
                <div className="max-w-7xl mx-auto">
                    <Outlet />
                </div>
            </main>

            {/* Bottom Navigation */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-background-dark border-t border-slate-200 dark:border-white/5 z-50 safe-area-bottom">
                <div className="flex items-center justify-around py-2 px-4 max-w-lg mx-auto">
                    <NavLink
                        to="/"
                        className={({ isActive }) =>
                            `flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition-all ${
                                isActive
                                    ? 'text-primary bg-primary/5'
                                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span className={`material-symbols-outlined text-[24px] ${isActive ? 'font-bold' : ''}`}>
                                    home
                                </span>
                                <span className="text-[10px] font-medium">Inicio</span>
                            </>
                        )}
                    </NavLink>

                    <NavLink
                        to="/inbox"
                        className={({ isActive }) =>
                            `flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition-all ${
                                isActive
                                    ? 'text-primary bg-primary/5'
                                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span className={`material-symbols-outlined text-[24px] ${isActive ? 'font-bold' : ''}`}>
                                    inbox
                                </span>
                                <span className="text-[10px] font-medium">Bandeja</span>
                            </>
                        )}
                    </NavLink>

                    <NavLink
                        to="/crm"
                        className={({ isActive }) =>
                            `flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition-all ${
                                isActive
                                    ? 'text-primary bg-primary/5'
                                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span className={`material-symbols-outlined text-[24px] ${isActive ? 'font-bold' : ''}`}>
                                    group
                                </span>
                                <span className="text-[10px] font-medium">CRM</span>
                            </>
                        )}
                    </NavLink>

                    <NavLink
                        to="/treatments"
                        className={({ isActive }) =>
                            `flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition-all ${
                                isActive
                                    ? 'text-primary bg-primary/5'
                                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span className={`material-symbols-outlined text-[24px] ${isActive ? 'font-bold' : ''}`}>
                                    dentistry
                                </span>
                                <span className="text-[10px] font-medium">Planes</span>
                            </>
                        )}
                    </NavLink>

                    <NavLink
                        to="/calendar"
                        className={({ isActive }) =>
                            `relative flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition-all ${
                                isActive
                                    ? 'text-primary bg-primary/5'
                                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span className={`material-symbols-outlined text-[24px] ${isActive ? 'font-bold' : ''}`}>
                                    calendar_month
                                </span>
                                <span className="text-[10px] font-medium">Agenda</span>
                            </>
                        )}
                    </NavLink>

{!isAgent && (
                    <NavLink
                        to="/marketing"
                        className={({ isActive }) =>
                            `flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition-all ${
                                isActive
                                    ? 'text-primary bg-primary/5'
                                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span className={`material-symbols-outlined text-[24px] ${isActive ? 'font-bold' : ''}`}>
                                    campaign
                                </span>
                                <span className="text-[10px] font-medium">Marketing</span>
                            </>
                        )}
                    </NavLink>
)}
                </div>
            </nav>

            {/* Click outside to close any menu */}
            {(showUserMenu || showNotifications) && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={closeAllMenus}
                />
            )}

            {/* Feedback Modal */}
            {showFeedbackModal && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowFeedbackModal(false)}></div>
                    <div className="relative bg-white dark:bg-slate-card rounded-2xl p-6 max-w-md w-full shadow-xl">
                        {feedbackSent ? (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 rounded-full bg-brand-accent/10 flex items-center justify-center mx-auto mb-4">
                                    <span className="material-symbols-outlined text-brand-accent text-[40px]">check_circle</span>
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">¡Gracias por tu feedback!</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Tu opinión nos ayuda a mejorar Diana IA.</p>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                            <span className="material-symbols-outlined text-primary">rate_review</span>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Enviar Feedback</h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Tu opinión es muy importante</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowFeedbackModal(false)}
                                        className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-600"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">close</span>
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
                                            ¿Cómo podemos mejorar Diana IA?
                                        </label>
                                        <textarea
                                            value={feedbackText}
                                            onChange={(e) => setFeedbackText(e.target.value)}
                                            placeholder="Cuéntanos tu experiencia, sugerencias o reporta algún problema..."
                                            rows={4}
                                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-sm placeholder:text-slate-400 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none dark:text-white"
                                        />
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setShowFeedbackModal(false)}
                                            className="flex-1 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleSendFeedback}
                                            disabled={!feedbackText.trim()}
                                            className="flex-1 py-2.5 text-sm font-semibold text-white bg-primary rounded-xl shadow-lg shadow-primary/30 hover:bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Enviar Feedback
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
        <SupportChat />
        </>
    );
};

export default Layout;
